import { MultipleOptionsResponseDto } from "@se/platform/forms/dtos/out/responses/multipleOptionsResponseDto";
import { fromEvent, Subject } from "rxjs";
import { debounceTime, pluck } from "rxjs/operators";
import { IMessagingService } from "../services";
import { Component } from "./component";
import { hide, show } from "../utils";

class CustomDropdownComponent extends Component {
    private wrapperEl: HTMLElement;
    private buttonEl: HTMLButtonElement;
    private buttonElInitText: string;
    private buttonResetEl: HTMLButtonElement;
    private optionEls: HTMLLIElement[];
    private inputEls: HTMLInputElement[];
    private dummyEl: HTMLSelectElement;
    private focusedOptionIndex$ = new Subject<number>();
    private focusedOptionIndex: number = -1;
    private onDocKeydownBound: (e: KeyboardEvent) => void;
    private isExpanded: boolean = false;
    private isFocused: boolean = false;

    constructor(protected fieldEl: HTMLElement, messagingService: IMessagingService) {
        super(fieldEl, messagingService);

        this.wrapperEl = fieldEl.querySelector(".js-form-field-dropdown-wrapper");
        this.buttonEl = fieldEl.querySelector(".js-form-field-dropdown-button");
        this.buttonElInitText = this.buttonEl.innerText;
        this.buttonResetEl = fieldEl.querySelector(".js-form-field-dropdown-button-reset");
        this.optionEls = Array.from(fieldEl.querySelectorAll(".js-form-field-dropdown-option"));
        this.inputEls = Array.from(fieldEl.querySelectorAll<HTMLInputElement>(".js-form-field-dropdown-option-checkbox"));
        this.dummyEl = fieldEl.querySelector<HTMLSelectElement>(".js-checkboxDropdown-component-dummy");

        this.onDocKeydownBound = this.onDocKeydown.bind(this);

        this.buttonEl.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.isExpanded === false) {
                this.expand();
            } else if (this.isExpanded === true) {
                this.collapse();
            }
        });

        this.buttonEl.addEventListener("focus", () => {
            if (this.isFocused === false) {
                document.addEventListener("keydown", this.onDocKeydownBound, true);
                this.isFocused = true;
            }
        });

        this.buttonResetEl.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.resetSelection();
            this.buttonEl.focus();
            this.collapse();
        });

        this.wrapperEl.addEventListener("focusout", this.onFocusout.bind(this));

        this.inputEls.forEach((checkboxEl) => {
            // listen for any of the checkbox fields changing, and extract its value
            const onCheckboxClicked$ = fromEvent(checkboxEl, "change").pipe(pluck("target", "value"),
                debounceTime(100)
            );

            onCheckboxClicked$.subscribe((value: string) => {
                const selected = this.inputEls.filter(a => a.checked && a.value !== '');
                const selectedOptions = selected.map(checkbox => checkbox.value);
                const selectedOptionsLabels = selected.map(checkbox => {
                    const label = this.wrapperEl.querySelector(`label[for="${checkbox.id}"]`);
                    return label ? label.textContent : checkbox.value;
                });
                    
                // Show clear [x] button when at least one selected, or hide
                this.inputEls.find(inputEl => inputEl.checked) ? show(this.buttonResetEl) : hide(this.buttonResetEl);
            
                this.setDisplayValue(selectedOptionsLabels);
                this.updateDummyValue(selectedOptions);
            });
        });

        this.focusedOptionIndex$.subscribe((index) => {
            this.focusedOptionIndex = index;
            this.hoverOption(index);
        });

        const onDummyElchanged$ = fromEvent(this.dummyEl, "change").pipe(
            pluck("target", "value"),
            debounceTime(100)
        );
        onDummyElchanged$.subscribe((value: string) => {
            const selectedOptions = Array.from(this.dummyEl.selectedOptions).map(option => option.value);
            this.receiveDummyValueUpdateHandler(selectedOptions);
        });

        // Dispatch a change event to synchronize initial state
        const event = new Event("change", { bubbles: true });
        this.dummyEl.dispatchEvent(event);
        
    }

    // update the value of this field in the DOM.
    protected setDisplayValue(value: string[]) {
        if (value.length === 0) {
            this.buttonEl.textContent = this.buttonElInitText;
        } else if (value.length === 1) {
            this.buttonEl.textContent = value[0];
        } else {
            this.buttonEl.textContent = `${value.length} selected`;
        }
        // this.inputEls.forEach((el) => {
        //     // el.checked = value.includes(el.value);
        // });
    }
    
    protected areEqual(prev: MultipleOptionsResponseDto, next: MultipleOptionsResponseDto) {
        if (next.value.length != prev.value.length) {
            return false;
        }

        const sortedPrevValues = prev.value.sort();
        const sortedNextValues = next.value.sort();

        // loop over our sorted prev/next values and compare each item.
        for (let i = 0; i < sortedPrevValues.length; i++) {
            if (sortedPrevValues[i] != sortedNextValues[i]) {
                return false;
            }
        }

        return true;
    }

    onFocusout(e: FocusEvent) {
        if (e.relatedTarget && this.wrapperEl.contains(e.relatedTarget as HTMLElement)) return;
        document.removeEventListener("keydown", this.onDocKeydownBound, true);
        this.focusedOptionIndex = -1;
        this.collapse();
        this.isFocused = false;
    }

    selectOption(index: number) {
        if (index > -1) {
            this.inputEls[index].checked = !this.inputEls[index].checked;
            this.optionEls[index].setAttribute("aria-selected", this.inputEls[index].checked.toString());
            const changeEvent = new Event("change");
            this.inputEls[index].dispatchEvent(changeEvent);
        }
    }

    expand() {
        this.containerEl.classList.add("form-field--dropdown--expanded");
        this.buttonEl.setAttribute("aria-expanded", "true");
        this.isExpanded = true;
    }

    collapse() {
        this.containerEl.classList.remove("form-field--dropdown--expanded");
        this.buttonEl.setAttribute("aria-expanded", "false");
        this.isExpanded = false;
    }

    hoverOption(index: number) {
        this.optionEls.forEach((el, i) => {
            if (index === i) {
                el.setAttribute("aria-selected", "true");
                el.focus();
            } else {
                el.setAttribute("aria-selected", "false");
            }
        });
    }

    onDocKeydown(e: KeyboardEvent) {
        if (e.target === this.buttonResetEl) return;
        switch (e.code) {
            case "ArrowDown": {
                this.onArrowDown(e);
                break;
            }
            case "ArrowUp": {
                this.onArrowUp(e);
                break;
            }
            case "Escape": {
                this.onEscape(e);
                break;
            }
            case "Space": {
                if (e.target !== this.buttonEl) this.onEnter(e);
                break;
            }
            case "Enter": {
                this.onEnter(e);
            }
            default:
                return;
        }
    }

    onArrowDown(e: KeyboardEvent) {
        e.preventDefault();
        if (this.focusedOptionIndex === -1) {
            this.expand();
        }
        if (this.focusedOptionIndex < this.optionEls.length - 1) {
            this.focusedOptionIndex$.next(this.focusedOptionIndex + 1);
        }
    }

    onArrowUp(e: KeyboardEvent) {
        e.preventDefault();
        if (this.focusedOptionIndex === 0) {
            this.focusedOptionIndex$.next(-1);
            this.collapse();
            this.buttonEl.focus();
        }
        if (this.focusedOptionIndex > 0) {
            this.focusedOptionIndex$.next(this.focusedOptionIndex - 1);
        }
    }

    onEscape(e: KeyboardEvent) {
        this.focusedOptionIndex$.next(this.focusedOptionIndex - 1);
        this.collapse();
        this.buttonEl.focus();
    }

    onEnter(e: KeyboardEvent) {
        e.preventDefault();
        if (this.focusedOptionIndex === -1 && this.isExpanded === false) {
            this.expand();
        } else if (this.focusedOptionIndex === -1 && this.isExpanded === true) {
            this.collapse();
        } else if (this.focusedOptionIndex > -1 && this.focusedOptionIndex < this.optionEls.length) {
            this.selectOption(this.focusedOptionIndex);
        }
        this.buttonEl.focus();
    }

    resetSelection() {
        this.inputEls.forEach((inputEl) => {
            inputEl.checked = false;
            const changeEvent = new Event("change");
            inputEl.dispatchEvent(changeEvent);
        });
    }

    updateDummyValue(selectedOptions: string[]) {
        Array.from(this.dummyEl.options).forEach((inputEl) => {
            inputEl.selected = false;
        });
        Array.from(this.dummyEl.options).forEach(option => {
            // Compare the option's value in lowercase
            option.selected = selectedOptions.map(option => option.toLowerCase()).includes(option.value);
        });
    
        const event = new Event('change', {bubbles: true});
        this.dummyEl.dispatchEvent(event);
        this.collapse();
    }

    receiveDummyValueUpdateHandler(selectedOptions: string[]) {
        this.inputEls.forEach((inputEl) => {
            const shouldBeChecked = selectedOptions.map(option => option.toLowerCase()).includes(inputEl.value.toLowerCase());
            if (inputEl.checked !== shouldBeChecked) {
                inputEl.checked = shouldBeChecked;
                const changeEvent = new Event("change");
                inputEl.dispatchEvent(changeEvent);
            }
        });
    }
    
}

export { CustomDropdownComponent };
